import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Avatar,
  FormControlLabel,
  Switch,
  Paper,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Public as JurisdictionIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Cell } from 'recharts';
import { jurisdictionsApi } from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { useApp } from '../App';

const COLORS = ['#1B365D', '#C5A572', '#2E5090', '#D4BC94', '#0F1F38', '#A68B5B'];

const REGIONS = {
  CIS: 'СНГ',
  EUROPE: 'Европа',
  MIDDLE_EAST: 'Ближний Восток',
  ASIA: 'Азия',
  AMERICAS: 'Америка',
  OTHER: 'Другое',
};

const Jurisdictions = () => {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(true);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [summary, setSummary] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editJurisdiction, setEditJurisdiction] = useState(null);
  const [detailJurisdiction, setDetailJurisdiction] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [jurisdictionsRes, summaryRes] = await Promise.all([
        jurisdictionsApi.getAll(),
        jurisdictionsApi.getSummary(),
      ]);
      setJurisdictions(jurisdictionsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      showNotification('Ошибка загрузки юрисдикций', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (jurisdiction = null) => {
    setEditJurisdiction(
      jurisdiction || {
        code: '',
        name: '',
        fullName: '',
        region: 'OTHER',
        taxTreatyWithRussia: false,
        notes: '',
      }
    );
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editJurisdiction.id) {
        await jurisdictionsApi.update(editJurisdiction.id, editJurisdiction);
        showNotification('Юрисдикция обновлена', 'success');
      } else {
        await jurisdictionsApi.create(editJurisdiction);
        showNotification('Юрисдикция создана', 'success');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка сохранения', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить юрисдикцию?')) return;

    try {
      await jurisdictionsApi.delete(id);
      showNotification('Юрисдикция удалена', 'success');
      fetchData();
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка удаления', 'error');
    }
  };

  const handleToggleActive = async (jurisdiction) => {
    try {
      if (jurisdiction.isActive) {
        await jurisdictionsApi.deactivate(jurisdiction.id);
        showNotification('Юрисдикция деактивирована', 'success');
      } else {
        await jurisdictionsApi.activate(jurisdiction.id);
        showNotification('Юрисдикция активирована', 'success');
      }
      fetchData();
    } catch (error) {
      showNotification('Ошибка изменения статуса', 'error');
    }
  };

  const handleViewDetails = async (id) => {
    try {
      const response = await jurisdictionsApi.getById(id);
      setDetailJurisdiction(response.data);
    } catch (error) {
      showNotification('Ошибка загрузки деталей', 'error');
    }
  };

  const getRegionColor = (region) => {
    const colors = {
      CIS: 'primary',
      EUROPE: 'info',
      MIDDLE_EAST: 'warning',
      ASIA: 'secondary',
      AMERICAS: 'success',
      OTHER: 'default',
    };
    return colors[region] || 'default';
  };

  const chartData = summary.map((j) => ({
    name: j.code,
    value: j.totalValue,
    assets: j.assetCount,
  }));

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Юрисдикции
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Добавить юрисдикцию
        </Button>
      </Box>

      {/* Summary Chart */}
      {chartData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Распределение активов по юрисдикциям
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                  <YAxis dataKey="name" type="category" width={60} />
                  <RechartsTooltip
                    formatter={(value) => formatCurrency(value, 'USD')}
                    labelFormatter={(label) => `Юрисдикция: ${label}`}
                  />
                  <Bar dataKey="value" name="Стоимость">
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={3}>
        {/* Jurisdictions List */}
        <Grid item xs={12} md={detailJurisdiction ? 7 : 12}>
          <Card>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Код</TableCell>
                    <TableCell>Название</TableCell>
                    <TableCell>Регион</TableCell>
                    <TableCell>Налоговое соглашение</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell width={150}>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Загрузка...
                      </TableCell>
                    </TableRow>
                  ) : jurisdictions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        Нет юрисдикций
                      </TableCell>
                    </TableRow>
                  ) : (
                    jurisdictions.map((jurisdiction) => (
                      <TableRow
                        key={jurisdiction.id}
                        hover
                        onClick={() => handleViewDetails(jurisdiction.id)}
                        sx={{ cursor: 'pointer' }}
                        selected={detailJurisdiction?.id === jurisdiction.id}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                              <FlagIcon fontSize="small" />
                            </Avatar>
                            <Typography fontWeight={600}>{jurisdiction.code}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{jurisdiction.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={REGIONS[jurisdiction.region]}
                            size="small"
                            color={getRegionColor(jurisdiction.region)}
                          />
                        </TableCell>
                        <TableCell>
                          {jurisdiction.taxTreatyWithRussia ? (
                            <Chip label="Есть" size="small" color="success" />
                          ) : (
                            <Chip label="Нет" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={jurisdiction.isActive ? <ActiveIcon /> : <BlockIcon />}
                            label={jurisdiction.isActive ? 'Активна' : 'Неактивна'}
                            size="small"
                            color={jurisdiction.isActive ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Редактировать">
                            <IconButton size="small" onClick={() => handleOpenDialog(jurisdiction)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={jurisdiction.isActive ? 'Деактивировать' : 'Активировать'}>
                            <IconButton size="small" onClick={() => handleToggleActive(jurisdiction)}>
                              {jurisdiction.isActive ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Удалить">
                            <IconButton size="small" color="error" onClick={() => handleDelete(jurisdiction.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Detail Panel */}
        {detailJurisdiction && (
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <FlagIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{detailJurisdiction.code}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {detailJurisdiction.name}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton size="small" onClick={() => setDetailJurisdiction(null)}>
                    ×
                  </IconButton>
                </Box>

                {detailJurisdiction.fullName && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {detailJurisdiction.fullName}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Chip
                    label={REGIONS[detailJurisdiction.region]}
                    size="small"
                    color={getRegionColor(detailJurisdiction.region)}
                  />
                  {detailJurisdiction.taxTreatyWithRussia && (
                    <Chip label="Налоговое соглашение с РФ" size="small" color="success" />
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Statistics */}
                <Typography variant="subtitle2" gutterBottom>
                  Статистика
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 1.5, bgcolor: 'primary.main', color: 'white' }}>
                      <Typography variant="caption">Всего активов</Typography>
                      <Typography variant="h6">
                        {formatCurrency(detailJurisdiction.stats?.totalValue || 0, 'USD')}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Активов</Typography>
                      <Typography variant="h6">
                        {detailJurisdiction.stats?.assetCount || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={3}>
                    <Paper sx={{ p: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Структур</Typography>
                      <Typography variant="h6">
                        {detailJurisdiction.stats?.holderCount || 0}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Asset Type Breakdown */}
                {detailJurisdiction.stats?.byAssetType && Object.keys(detailJurisdiction.stats.byAssetType).length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom>
                      По типам активов
                    </Typography>
                    <Table size="small">
                      <TableBody>
                        {Object.entries(detailJurisdiction.stats.byAssetType).map(([type, data]) => (
                          <TableRow key={type}>
                            <TableCell>{type}</TableCell>
                            <TableCell align="right">{data.count} шт.</TableCell>
                            <TableCell align="right">{formatCurrency(data.value, 'USD')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}

                {detailJurisdiction.notes && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>
                      Заметки
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {detailJurisdiction.notes}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editJurisdiction?.id ? 'Редактировать юрисдикцию' : 'Добавить юрисдикцию'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                label="Код"
                value={editJurisdiction?.code || ''}
                onChange={(e) => setEditJurisdiction({ ...editJurisdiction, code: e.target.value.toUpperCase() })}
                inputProps={{ maxLength: 10 }}
                helperText="Например: RU, UAE, CY"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                required
                label="Название"
                value={editJurisdiction?.name || ''}
                onChange={(e) => setEditJurisdiction({ ...editJurisdiction, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Полное название"
                value={editJurisdiction?.fullName || ''}
                onChange={(e) => setEditJurisdiction({ ...editJurisdiction, fullName: e.target.value })}
                helperText="Например: Объединенные Арабские Эмираты"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Регион</InputLabel>
                <Select
                  value={editJurisdiction?.region || 'OTHER'}
                  label="Регион"
                  onChange={(e) => setEditJurisdiction({ ...editJurisdiction, region: e.target.value })}
                >
                  {Object.entries(REGIONS).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editJurisdiction?.taxTreatyWithRussia || false}
                    onChange={(e) =>
                      setEditJurisdiction({ ...editJurisdiction, taxTreatyWithRussia: e.target.checked })
                    }
                  />
                }
                label="Налоговое соглашение с РФ"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Заметки"
                value={editJurisdiction?.notes || ''}
                onChange={(e) => setEditJurisdiction({ ...editJurisdiction, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSave}>
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Jurisdictions;
